import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { AgentModule } from './actionTypes'

export interface CrudModuleBridge {
  openForm: (mode: 'new' | 'copy' | 'edit') => Promise<string>
  selectRow: (selector: string | number) => Promise<string>
  setField: (field: string, value: unknown) => Promise<string>
  saveForm: () => Promise<string>
  cancelForm: () => Promise<string>
  getState: () => Promise<string>
}

interface BridgeRegistryContextValue {
  activeModule: AgentModule | null
  setActiveModule: (module: AgentModule | null) => void
  registerBridge: (module: AgentModule, bridge: CrudModuleBridge) => void
  unregisterBridge: (module: AgentModule) => void
  getBridge: (module: AgentModule) => CrudModuleBridge | null
}

const BridgeRegistryContext = createContext<BridgeRegistryContextValue | null>(null)

export function AgentBridgeProvider({ children }: { children: React.ReactNode }) {
  const [activeModule, setActiveModule] = useState<AgentModule | null>(null)
  const [bridges, setBridges] = useState<Partial<Record<AgentModule, CrudModuleBridge>>>({})

  const registerBridge = useCallback((module: AgentModule, bridge: CrudModuleBridge) => {
    setBridges((prev) => ({ ...prev, [module]: bridge }))
  }, [])

  const unregisterBridge = useCallback((module: AgentModule) => {
    setBridges((prev) => {
      const next = { ...prev }
      delete next[module]
      return next
    })
  }, [])

  const getBridge = useCallback(
    (module: AgentModule) => {
      return bridges[module] ?? null
    },
    [bridges],
  )

  const value = useMemo(
    () => ({
      activeModule,
      setActiveModule,
      registerBridge,
      unregisterBridge,
      getBridge,
    }),
    [activeModule, registerBridge, unregisterBridge, getBridge],
  )

  return <BridgeRegistryContext.Provider value={value}>{children}</BridgeRegistryContext.Provider>
}

export function useAgentBridgeRegistry() {
  const ctx = useContext(BridgeRegistryContext)
  if (!ctx) throw new Error('useAgentBridgeRegistry must be used inside AgentBridgeProvider')
  return ctx
}
